import type { HomeStructureDefenseState, HomeStructurePotionSettings } from "@/home/home-structure-types";
import type { HomeStructureStatConfig } from "@/home/home-structure-stats";
import { homeStructureMaxHp } from "@/home/home-structure-stats";
import type { HomeStructureCardCounts } from "@/home/home-structure-types";

export function normalizeDefenseState(
  raw: Partial<HomeStructureDefenseState> | null | undefined,
): HomeStructureDefenseState {
  return {
    damageTaken: Math.max(0, Math.floor(raw?.damageTaken ?? 0)),
    lastDamageAt: Math.max(0, Math.floor(raw?.lastDamageAt ?? 0)),
    brokenUntil: Math.max(0, Math.floor(raw?.brokenUntil ?? 0)),
  };
}

export function structureCurrentHp(
  maxHp: number,
  damageTaken: number,
): number {
  return Math.max(0, maxHp - Math.max(0, Math.floor(damageTaken)));
}

export function structureHpPercent(currentHp: number, maxHp: number): number {
  if (maxHp <= 0) return 100;
  return (currentHp / maxHp) * 100;
}

export function isStructureShieldActive(brokenUntil: number, now = Date.now()): boolean {
  return brokenUntil > now;
}

export function canAttackStructure(
  state: HomeStructureDefenseState,
  maxHp: number,
  now = Date.now(),
): boolean {
  if (isStructureShieldActive(state.brokenUntil, now)) return false;
  if (structureCurrentHp(maxHp, state.damageTaken) <= 0) return false;
  return true;
}

/** Passive regen when out of combat (not recently hit). */
export function applyStructurePassiveRegen(
  state: HomeStructureDefenseState,
  _cards: HomeStructureCardCounts,
  regenPerSec: number,
  config: HomeStructureStatConfig,
  now = Date.now(),
): HomeStructureDefenseState {
  if (regenPerSec <= 0 || state.damageTaken <= 0) return state;
  const delayMs = config.homeStructureRegenDelayAfterHitSec * 1000;
  if (state.lastDamageAt > 0 && now - state.lastDamageAt < delayMs) return state;
  const elapsedSec = state.lastDamageAt > 0 ? (now - state.lastDamageAt - delayMs) / 1000 : 0;
  if (elapsedSec <= 0 && state.lastDamageAt > 0) return state;
  const heal = Math.floor(regenPerSec * Math.max(0, elapsedSec));
  if (heal <= 0) return state;
  const nextDamage = Math.max(0, state.damageTaken - heal);
  return {
    ...state,
    damageTaken: nextDamage,
    lastDamageAt: nextDamage > 0 ? now - delayMs : 0,
  };
}

export function applyStructureDamage(
  state: HomeStructureDefenseState,
  damage: number,
  maxHp: number,
  config: HomeStructureStatConfig,
  now = Date.now(),
): HomeStructureDefenseState {
  const add = Math.max(0, Math.floor(damage));
  if (add <= 0) return { ...state, lastDamageAt: now };
  let damageTaken = Math.min(maxHp, state.damageTaken + add);
  let brokenUntil = state.brokenUntil;
  if (damageTaken >= maxHp) {
    const shieldMs = config.homeStructureShieldHours * 60 * 60 * 1000;
    brokenUntil = Math.max(brokenUntil, now + shieldMs);
  }
  return {
    damageTaken,
    lastDamageAt: now,
    brokenUntil,
  };
}

export function applyStructurePotionHeal(
  state: HomeStructureDefenseState,
  healAmount: number,
): HomeStructureDefenseState {
  const heal = Math.max(0, Math.floor(healAmount));
  if (heal <= 0) return state;
  return {
    ...state,
    damageTaken: Math.max(0, state.damageTaken - heal),
  };
}

export function applyAutoStructurePotions(
  state: HomeStructureDefenseState,
  cards: HomeStructureCardCounts,
  config: HomeStructureStatConfig,
  potion: HomeStructurePotionSettings,
): { state: HomeStructureDefenseState; potionsUsed: number; potionsLeft: number } {
  const maxHp = homeStructureMaxHp(cards, config);
  let damageTaken = state.damageTaken;
  let potionsLeft = Math.max(0, Math.floor(potion.potions));
  let potionsUsed = 0;
  if (!potion.autoEnabled || potionsLeft <= 0) {
    return { state, potionsUsed: 0, potionsLeft };
  }
  const heal = config.homeStructurePotionHeal;
  while (potionsLeft > 0 && damageTaken > 0) {
    const current = structureCurrentHp(maxHp, damageTaken);
    const pct = structureHpPercent(current, maxHp);
    if (pct > potion.autoHpPercent) break;
    damageTaken = Math.max(0, damageTaken - heal);
    potionsLeft -= 1;
    potionsUsed += 1;
  }
  return {
    state: { ...state, damageTaken },
    potionsUsed,
    potionsLeft,
  };
}
